import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { FileText, Calendar, MapPin, Ruler, ChevronRight, Menu, User, MessageSquare, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { AppSidebar } from "@/components/AppSidebar";
import { useProjects } from "@/hooks/useProjects";
import { NotificationBell } from "@/components/NotificationBell";

interface Inquiry {
  id: string;
  title: string;
  description: string | null;
  region: string | null;
  status: string;
  created_at: string;
}

export default function CustomerDashboard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [quoteCounts, setQuoteCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const { sites, chats, deleteChat, assignChat, addSite } = useProjects();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    const { data: inqs } = await supabase
      .from("inquiries")
      .select("id, title, description, region, status, created_at")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: false });

    setInquiries(inqs || []);

    if (inqs && inqs.length > 0) {
      const inquiryIds = inqs.map((i) => i.id);
      const { data: allQuotes } = await supabase
        .from("supplier_quotes")
        .select("id, inquiry_id")
        .in("inquiry_id", inquiryIds);

      const counts: Record<string, number> = {};
      for (const q of allQuotes || []) {
        counts[q.inquiry_id] = (counts[q.inquiry_id] || 0) + 1;
      }
      setQuoteCounts(counts);
    }

    setLoading(false);
  };

  const parseDescription = (desc: string | null): Record<string, string> => {
    if (!desc) return {};
    const result: Record<string, string> = {};
    for (const line of desc.split("\n")) {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim();
        if (val && val !== "null") result[key] = val;
      }
    }
    return result;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "open":
        return t("customerDashboard.statusOpen");
      case "in_progress":
        return t("customerDashboard.statusInProgress");
      case "closed":
        return t("customerDashboard.statusClosed");
      case "completed":
        return t("customerDashboard.statusCompleted");
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">...</div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onClose={() => setSidebarOpen(false)}
        isLoggedIn={true}
        sites={sites}
        chats={chats}
        onNewChat={() => navigate("/")}
        onSelectChat={() => navigate("/")}
        onDeleteChat={async (id) => { await deleteChat(id); }}
        onAssignChat={async (chatId, siteId) => { await assignChat(chatId, siteId); }}
        onAddSite={async (name) => { await addSite(name); }}
      />

      <div className={`flex-1 flex flex-col h-full overflow-hidden transition-all duration-200 ${sidebarOpen ? "lg:ml-[260px]" : ""}`}>
        <header className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-0.5 sm:gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0 ${sidebarOpen ? "lg:hidden" : ""}`}
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity">
              <Logo size={32} />
              <span className="font-semibold">cenik.ai</span>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/profile" className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground">
              <User className="w-5 h-5" />
            </Link>
            <NotificationBell />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
            <div className="mb-4 flex items-center gap-2">
              <button onClick={() => navigate("/")} className="p-2 rounded-full hover:bg-accent transition-colors">
                <ArrowLeft className="w-4 h-4 text-foreground" />
              </button>
              <span className="font-semibold text-foreground text-sm flex-1">{t("customerDashboard.title")}</span>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {t("customerDashboard.newInquiry")}
              </Link>
            </div>

            {/* Onboarding blok nahoře při prázdném stavu */}
            {inquiries.length === 0 && (
              <div className="mb-6 border border-border rounded-xl bg-muted/40 px-4 py-3.5 text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1.5">{t("customerDashboard.onboardingTitle")}</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>{t("customerDashboard.onboardingStep1")}</li>
                  <li>{t("customerDashboard.onboardingStep2")}</li>
                  <li>{t("customerDashboard.onboardingStep3")}</li>
                </ol>
              </div>
            )}

            {inquiries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">{t("customerDashboard.emptyTitle")}</p>
                <p className="text-muted-foreground/60 text-xs mt-1">{t("customerDashboard.emptyDesc")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {inquiries.map((inq) => {
                  const details = parseDescription(inq.description);
                  const quoteCount = quoteCounts[inq.id] || 0;
                  return (
                    <div
                      key={inq.id}
                      className="border border-border rounded-xl p-4 bg-card hover:bg-accent/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/dashboard/${inq.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${
                          quoteCount > 0 ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                        }`}>
                          <span className="text-lg font-bold leading-none">{quoteCount}</span>
                          <span className="text-[9px] font-medium leading-tight mt-0.5">
                            {quoteCount === 1 ? t("customerDashboard.quote") : quoteCount < 5 && quoteCount > 0 ? t("customerDashboard.quotes") : t("customerDashboard.quotesMany")}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-foreground text-sm">{inq.title}</h3>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                              inq.status === "open"
                                ? "bg-green-100 text-green-700"
                                : inq.status === "completed"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-muted text-muted-foreground"
                            }`}>
                              {getStatusLabel(inq.status)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            {details["Plocha"] && (
                              <span className="flex items-center gap-1">
                                <Ruler className="w-3 h-3" />
                                {details["Plocha"]}
                              </span>
                            )}
                            {inq.region && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {inq.region}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(inq.created_at).toLocaleDateString("cs-CZ")}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
